import { useActionsContext } from "./ActionsContext";
import { ActionRowDefinition } from "./types";

/**
 * Count of drawer transactions that belong to this action row's bucket.
 *
 * Bucket membership is derived from `newValuesMap` only: selection is a
 * pure UX gesture in the new drawer and does not affect action representation.
 *
 * - Non-base rows: count = number of transactions in `newValuesMap` whose
 *   stringified value matches the row's action value (exposed via
 *   `ActionsContext.rowIdToOwnedTxIds`).
 * - Base row: count = `totalTransactions - Σ(non-base owned ids)`. The base
 *   row never tracks ids individually because the confirm flow applies it
 *   via a filter-based payload, not a per-id list.
 */
export function useActionRowBucketCount(row: ActionRowDefinition): number {
    const { rowIdToOwnedTxIds, rows, totalTransactions } = useActionsContext();

    if (row.isBaseAction) {
        let overridden = 0;
        for (const r of rows) {
            if (r.isBaseAction) {
                continue;
            }
            overridden +=
                r.filterScope?.transactionCount ??
                rowIdToOwnedTxIds.get(r.id)?.length ??
                0;
        }
        return Math.max(totalTransactions - overridden, 0);
    }

    if (row.filterScope) {
        return row.filterScope.transactionCount;
    }

    return rowIdToOwnedTxIds.get(row.id)?.length ?? 0;
}
